import * as toastr from 'toastr';

import * as Models from '../Models';
import * as Services from '../Services';

export interface IDocumentManagerDirectiveScope extends ng.IScope {
  documentManagerDirective: DocumentManagerCtrl;
  key: any;
  type: any;
  controllerFnOnUpload: any;
  searchEnabled: boolean;
  reloadState: any;
  isModal: boolean;
  canUpload: boolean;
  canClear: boolean;
  search: any;
  modalCloseFunction: any;
  documentUploadModel: any;
  acceptTypes: any;
}

export class DocumentManagerDirective implements ng.IDirective {
  public restrict: string = "E";
  public template = require("./document-manager-directive.html");
  public controller: string = "DocumentManagerCtrl";
  public controllerAs: string = "documentManagerDirective";
  public scope = {
    key: "@",
    type: "@",
    searchEnabled: "=",
    canUpload: "=?",
    canClear: "=?",
    isModal: "=",
    reloadState: "=?",
    modalCloseFunction: "&",
    controllerFnOnUpload: "&",
    documentUploadModel: "=?",
    acceptTypes: "=?",
  };
}

export class DocumentManagerCtrl {
  public search: any;
  public documentTypes: Models.DocumentManagement.SolarDocumentType.ISolarDocumentType[];
  public allDocuments: Models.DocumentManagement.Document.IDocument[];
  public documentsToDisplay: Models.DocumentManagement.Document.IDocument[];
  public showArchivedButton = new Models.DocumentManagement.ShowArchivedButton.ShowArchivedButton();
  private submittingNewDocument = false;
  private uploadInProgress = false;
  private uploadProgressPercent = 0;
  private serviceProgressPercent = 0;
  public optionsForType: any;
  public customerDocumentTypes: string[];

  static $inject = ["$scope", "$state", "DocumentApiClient", "Blob", "FileSaver", "igs.maskingService"];
  constructor(
    protected $scope: IDocumentManagerDirectiveScope,
    protected $state: angular.ui.IStateService,
    protected documentApiClient: Services.DocumentApiClient.IDocumentApiClient,
    protected blob: Blob,
    protected fileSaver: any,
    protected maskingService: any,
  ) {
    this.setType($scope.type);
    this.setupDefaultCustomerDocumentUploadModel();
    if (this.optionsForType.enableDocumentTypes) {
      this.populateDocumentTypeOptions();
    }
    this.getallDocuments();
  }

  public setType = (type: string): void => {
    this.optionsForType = {
      updateDocumentFileNameMethod: this.documentApiClient.updateDocumentFileName,
      downloadMethod: this.documentApiClient.getDocumentForDownload,
      archiveDocumentRequest: Models.DocumentManagement.ArchiveDocumentRequest,
      archiveMethod: this.documentApiClient.archiveDocument,
    };
    switch (type) {
      case "issue":
        //this.key is issueId
        this.optionsForType.uploadDocumentMethod = this.documentApiClient.uploadIssueDocument;
        this.optionsForType.getDocumentTypes = null;
        this.optionsForType.getDocuments = this.documentApiClient.getIssueDocuments;
        this.optionsForType.uploadDocumentModel =
          Models.DocumentManagement.CustomerDocumentUploadModel.IssueDocumentUploadModel;
        this.optionsForType.enableDocumentTypes = false;
        break;
      case "customer":
        //this.key is igsCustomerNumber
        this.optionsForType.uploadDocumentMethod = this.documentApiClient.uploadCustomerDocument;
        this.optionsForType.getDocumentTypes = this.documentApiClient.getSolarDocumentTypes;
        this.optionsForType.getDocuments = this.documentApiClient.getCustomerDocuments;
        this.optionsForType.uploadDocumentModel = Models.DocumentManagement.CustomerDocumentUploadModel;
        this.optionsForType.enableDocumentTypes = true;
        break;
      case "inline":
        //this.key is igsCustomerNumber
        this.optionsForType.uploadDocumentMethod = this.documentApiClient.uploadCustomerDocument;
        this.optionsForType.getDocumentTypes = null;
        this.optionsForType.getDocuments = null;
        this.optionsForType.uploadDocumentModel = Models.DocumentManagement.CustomerDocumentUploadModel;
        this.optionsForType.enableDocumentTypes = false;
        break;
      default:
        toastr.error("Type is not recognized");
        break;
    }
  };

  public updateFileName = (documentGuid: string): void => {
    var newFileName = (document.getElementById(documentGuid + "-name-input") as HTMLInputElement).value;

    const updateDocumentFileNameRequest =
      new Models.DocumentManagement.UpdateDocumentFileNameRequest.UpdateDocumentFileNameRequest(
        newFileName,
        documentGuid,
        this.$scope.key,
        this.$scope.type,
      );

    this.optionsForType
      .updateDocumentFileNameMethod(updateDocumentFileNameRequest)
      .then((allDocuments: Models.DocumentManagement.Document.IDocument[]) => {
        this.allDocuments = allDocuments;
        this.assignViewableDocs();
      });
  };

  public uploadFile = (): void => {
    if (!this.$scope.documentUploadModel.file) {
      return;
    }

    console.log("uploadFile");
    this.uploadInProgress = true;
    this.maskingService.mask();
    var interval = setInterval(this.updateServiceProgressStatus, 1800);

    this.optionsForType
      .uploadDocumentMethod(this.$scope.documentUploadModel)
      .progress((uploadProgress: any) => {
        this.uploadProgressPercent = (uploadProgress.loaded / uploadProgress.total) * 100;
      })
      .then(
        (res) => {
          this.getallDocuments();
          this.setupDefaultCustomerDocumentUploadModel();
          clearInterval(interval);
          this.submittingNewDocument = false;
          this.uploadInProgress = false;
          this.maskingService.unmask();
          if (this.$scope.controllerFnOnUpload) {
            this.$scope.controllerFnOnUpload();
          }
          if (this.$scope.reloadState) {
            this.$state.reload();
          }
        },
        (error) => {
          toastr.error("Internal server error. File failed to upload.");
          this.setupDefaultCustomerDocumentUploadModel();
          clearInterval(interval);
          this.submittingNewDocument = false;
          this.uploadInProgress = false;
          this.maskingService.unmask();
          if (this.$scope.reloadState) {
            this.$state.reload();
          }
        },
      );
  };

  public updateServiceProgressStatus = (): void => {
    if (this.uploadInProgress && this.uploadProgressPercent === 100 && this.serviceProgressPercent < 100) {
      this.serviceProgressPercent = this.serviceProgressPercent + 10;
      this.$scope.$apply();
    }
  };

  public downloadDocument = (documentGuid: string): void => {
    this.optionsForType
      .downloadMethod(documentGuid)
      .then((documentDownload: Models.DocumentManagement.DocumentDownload.IDocumentDownload) => {
        this.blob = this.b64ToBlob(documentDownload.fileData, documentDownload.mimeType);
        this.fileSaver.saveAs(this.blob, documentDownload.fileName);
      });
  };

  public archiveDocument = (documentKey: string): void => {
    const archiveDocumentRequest = new Models.DocumentManagement.ArchiveDocumentRequest.ArchiveDocumentRequest(
      documentKey,
      this.$scope.key,
      this.$scope.type,
    );
    this.optionsForType.archiveMethod(archiveDocumentRequest).then(() => {
      this.getallDocuments();
      if (this.$scope.reloadState) {
        this.$state.reload();
      }
    });
  };

  public toggleShowArchived = () => {
    this.showArchivedButton.enabled = !this.showArchivedButton.enabled;
    this.assignViewableDocs();
  };

  public cancelUpload = (): void => {
    this.submittingNewDocument = false;
    this.setupDefaultCustomerDocumentUploadModel();
  };

  public processUserFileSelect = (): void => {
    if (this.$scope.acceptTypes && !this.$scope.acceptTypes.includes(this.$scope.documentUploadModel.file.type)) {
      throw new Error("Invalid file type - only image files are allowed");
    }
    if (!this.$scope.documentUploadModel.file) {
      this.setupDefaultCustomerDocumentUploadModel();
    } else {
      this.setFileTypeAndDefaultUserDefinedFileName();
    }
    this.submittingNewDocument = true;
  };

  private setFileTypeAndDefaultUserDefinedFileName = (): void => {
    var fileNameEnd = this.$scope.documentUploadModel.file.name.lastIndexOf(".");
    var defaultUserInputFileName = this.$scope.documentUploadModel.file.name.substring(0, fileNameEnd);
    this.$scope.documentUploadModel.userInputFileName = defaultUserInputFileName;

    this.$scope.documentUploadModel.fileExtension = this.$scope.documentUploadModel.file.name.substring(fileNameEnd);
  };

  private b64ToBlob = (b64Data: string, contentType: string): Blob => {
    contentType = contentType || "";
    var sliceSize = 512;

    var byteCharacters = atob(b64Data);
    var byteArrays: any = [];

    for (var offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      var slice = byteCharacters.slice(offset, offset + sliceSize);

      var byteNumbers = new Array(slice.length);
      for (var i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      var byteArray = new Uint8Array(byteNumbers);

      byteArrays.push(byteArray);
    }

    var blob = new Blob(byteArrays, { type: contentType });
    return blob;
  };

  private removeFileExtension = (filename: string): string => {
    var indexOfLastFullStop = filename.lastIndexOf(".");
    return indexOfLastFullStop !== -1 ? filename.substring(0, indexOfLastFullStop) : filename;
  };

  private populateDocumentTypeOptions = (): void => {
    this.optionsForType
      .getDocumentTypes(this.$scope.key)
      .then((documentTypes: Models.DocumentManagement.SolarDocumentType.SolarDocumentType[]) => {
        this.documentTypes = documentTypes;
      });
  };

  private getallDocuments = () => {
    if (this.optionsForType.getDocuments) {
      this.optionsForType.getDocuments(this.$scope.key).then((allDocuments: any) => {
        this.allDocuments = allDocuments;
        this.assignViewableDocs();
      });
    }
  };

  private assignViewableDocs = () => {
    if (!this.showArchivedButton.enabled) {
      this.documentsToDisplay = this.allDocuments.filter((x) => x.isArchived === false);
    } else {
      this.documentsToDisplay = this.allDocuments;
    }

    this.customerDocumentTypes = this.documentsToDisplay
      .map((x) => x.documentTypeName)
      .filter((value, index, self) => self.indexOf(value) === index);
  };

  private setupDefaultCustomerDocumentUploadModel = (): void => {
    const docType = null;
    this.$scope.documentUploadModel =
      new Models.DocumentManagement.CustomerDocumentUploadModel.CustomerDocumentUploadModel(docType);
    this.$scope.documentUploadModel.file = null;
    this.uploadProgressPercent = 0;
    this.serviceProgressPercent = 0;
    this.$scope.documentUploadModel.key = this.$scope.key;
  };
}
